/**
 * Shuttle admin panel — top-level UI for the shuttle SDK control surface.
 *
 * Renders a tenant selector + tab strip (Layers, Quotas, Settings) into a single container.
 * Each sub-tab owns its own DOM sub-tree and wires its own event listeners.
 * Shared state lives in this module's local closure (selectedTenant, etc.).
 *
 * @module panels/shuttle
 */
import * as api from "../../api/shuttle.ts";
import { listTenants } from "../../api/tenants.ts";
import {
	setTenantSlug,
	tenantSlug,
} from "../../features/new-dashboard/tenant.ts";
import type { MapLayer, ScheduleEntry } from "../../types/shuttle.ts";
import { renderAppsTab } from "./apps.ts";
import { renderLayersTab } from "./layers.ts";
import { renderQuotasTab } from "./quotas.ts";
import { renderSettingsTab } from "./settings.ts";

/**
 * Tenant list — loaded once from the auth registry on first render, filtered
 * to tenants with the "shuttle" capability enabled.
 * Falls back to the empty list if the call fails (e.g. user is not superadmin).
 */
let _tenants: { slug: string; name: string }[] = [];
let _tenantsLoaded = false;

async function ensureTenants(): Promise<void> {
	if (!_tenantsLoaded) {
		try {
			const all = await listTenants();
			_tenants = all
				.filter((t) => t.capabilities?.includes("shuttle"))
				.map((t) => ({ slug: t.slug, name: t.name }));
		} catch {
			_tenants = [];
		}
		_tenantsLoaded = true;
	}

	const globalSlug = tenantSlug();
	const isShuttleCapable = _tenants.some((t) => t.slug === globalSlug);

	if (globalSlug && isShuttleCapable) {
		state.tenantSlug = globalSlug;
	} else if (_tenants.length > 0) {
		state.tenantSlug = _tenants[0].slug;
		setTenantSlug(state.tenantSlug);
	} else {
		state.tenantSlug = "";
	}
}

type Tab = "layers" | "quotas" | "apps" | "settings";

interface State {
	containerId: string;
	tenantSlug: string;
	tab: Tab;
}

const state: State = {
	containerId: "",
	tenantSlug: "", // populated by ensureTenants() on first render
	tab: "layers",
};

/** Cached per-tenant data so tabs can share without refetching every flip. */
export interface ShuttleData {
	layers: MapLayer[];
	schedules: Map<string, ScheduleEntry[]>; // kept for type compatibility
}

const cache: Map<string, ShuttleData> = new Map();

function emptyData(): ShuttleData {
	return { layers: [], schedules: new Map() };
}

export function getCache(slug: string): ShuttleData {
	let d = cache.get(slug);
	if (!d) {
		d = emptyData();
		cache.set(slug, d);
	}
	return d;
}

export function invalidateCache(slug: string): void {
	cache.delete(slug);
}

export async function refreshAll(slug: string): Promise<ShuttleData> {
	const d = emptyData();
	const [layers] = await Promise.all([api.listLayers(slug).catch(() => [])]);
	d.layers = layers;
	cache.set(slug, d);
	return d;
}

/**
 * Render the shuttle admin panel into the given container. Idempotent — call
 * again to switch tabs/tenant or after an action mutates state.
 */
export async function renderShuttlePanel(containerId: string): Promise<void> {
	state.containerId = containerId;
	const root = document.getElementById(containerId);
	if (!root) return;

	await ensureTenants();

	// Top-level frame: tenant select + tab strip + body container.
	root.innerHTML = `
    <div class="shuttle-toolbar">
      <label class="shuttle-toolbar-label">Tenant</label>
      <select id="shuttle-tenant-select" class="shuttle-select">
        ${
					_tenants.length === 0
						? `<option value="">No tenants found — log in as superadmin</option>`
						: _tenants
								.map(
									(t) => `
          <option value="${t.slug}" ${t.slug === state.tenantSlug ? "selected" : ""}>${t.name}</option>
        `,
								)
								.join("")
				}
      </select>
      <button id="shuttle-refresh" class="shuttle-btn">Refresh</button>
    </div>

    <div class="shuttle-tabs">
      ${(["layers", "quotas", "apps", "settings"] as Tab[])
				.map((t) => {
					let label = t.charAt(0).toUpperCase() + t.slice(1);
					if (t === "apps") label = "App Registration";
					return `
        <button class="shuttle-tab ${t === state.tab ? "active" : ""}" data-tab="${t}">
          ${label}
        </button>
      `;
				})
				.join("")}
    </div>

    <div id="shuttle-tab-body" class="shuttle-tab-body">Loading…</div>
  `;

	root
		.querySelector("#shuttle-tenant-select")
		?.addEventListener("change", (e) => {
			const val = (e.target as HTMLSelectElement).value;
			state.tenantSlug = val;
			setTenantSlug(val);
			invalidateCache(state.tenantSlug);
			renderShuttlePanel(containerId);
		});

	root
		.querySelector("#shuttle-refresh")
		?.addEventListener("click", async () => {
			invalidateCache(state.tenantSlug);
			await renderShuttlePanel(containerId);
		});

	root.querySelectorAll(".shuttle-tab").forEach((btn) => {
		btn.addEventListener("click", () => {
			state.tab = (btn as HTMLElement).dataset.tab as Tab;
			renderShuttlePanel(containerId);
		});
	});

	// Ensure data is loaded before delegating to the active tab.
	let data = cache.get(state.tenantSlug);
	if (!data) {
		data = await refreshAll(state.tenantSlug);
	}

	const body = root.querySelector("#shuttle-tab-body") as HTMLElement;
	const ctx = {
		tenantSlug: state.tenantSlug,
		data,
		// Invalidate the cache so any mutation triggers a fresh fetch
		rerender: () => {
			invalidateCache(state.tenantSlug);
			return renderShuttlePanel(containerId);
		},
	};

	switch (state.tab) {
		case "layers":
			await renderLayersTab(body, ctx);
			break;
		case "quotas":
			await renderQuotasTab(body, ctx);
			break;
		case "apps":
			await renderAppsTab(body, ctx);
			break;
		case "settings":
			await renderSettingsTab(body, ctx);
			break;
	}
}

export interface TabContext {
	tenantSlug: string;
	data: ShuttleData;
	rerender: () => Promise<void>;
}
